import { Link } from "react-router-dom";
import { ShieldCheck, Globe2, Copy, FileCheck2, Layers, ArrowRight } from "lucide-react";

const SECTIONS = [
  {
    Icon: ShieldCheck,
    title: "Vérification de propriété du site",
    intro: "Avant tout scan surveillé, EmpCo doit prouver que vous êtes bien le propriétaire du domaine. Trois méthodes au choix.",
    steps: [
      { name: "Balise HTML", body: "Ajoutez une balise <meta name=\"empco-verify\" content=\"…\" /> dans le <head> de votre page d'accueil. C'est la méthode la plus rapide pour un CMS type WordPress/Shopify." },
      { name: "Enregistrement DNS TXT", body: "Créez un enregistrement TXT sur votre zone DNS avec la valeur fournie. Fonctionne même si le site n'est pas encore en ligne. Propagation : 5 à 30 min." },
      { name: "Fichier /.well-known", body: "Déposez le fichier de vérification à la racine du site (https://votresite.fr/.well-known/empco-verify). Idéal pour un site statique ou un CDN." },
    ],
  },
  {
    Icon: Globe2,
    title: "Ajouter un site — bouton « + Site »",
    intro: "Depuis « Sites surveillés », cliquez sur « + Site » pour ajouter un domaine à votre périmètre de surveillance.",
    steps: [
      { name: "Domaine principal ou sous-domaine", body: "Un site = un domaine (ou sous-domaine). Ex : evian.com et bio.evian.com comptent comme deux sites. Vous pouvez surveiller jusqu'à 3 sites dans l'offre Surveillance." },
      { name: "Fréquence de recrawl", body: "Choisissez « Chaque semaine » (par défaut) ou « Chaque mois » selon la volatilité de votre contenu marketing. Les alertes sont générées à chaque recrawl si une nouvelle allégation apparaît." },
      { name: "Vérification propriété", body: "Une fois le site ajouté, vérifiez la propriété (voir ci-dessus). Tant que le site n'est pas vérifié, seuls les scans manuels sont possibles." },
    ],
  },
  {
    Icon: Copy,
    title: "Détecter les doublons",
    intro: "L'outil « Détecter doublons » repère les allégations identiques ou proches sur plusieurs pages ou sites, pour vous éviter de multiplier les preuves.",
    steps: [
      { name: "Analyse vectorielle", body: "Chaque allégation est convertie en vecteur (hash + similarité cosinus). Les paires ≥ 92 % de similarité sont fusionnées automatiquement." },
      { name: "Preuves mutualisées", body: "Une preuve attachée à une allégation « mère » couvre toutes les copies détectées dans le registre. Gain de temps × 10 sur les gros catalogues." },
      { name: "Historique versionné", body: "Chaque modification est horodatée et versionnée : le doublon détecté n'écrase jamais l'original, il y est rattaché." },
    ],
  },
  {
    Icon: FileCheck2,
    title: "Générer le dossier de contrôle DGCCRF",
    intro: "En 1 clic, EmpCo génère un PDF horodaté prêt à être remis à un contrôleur ou à votre avocat.",
    steps: [
      { name: "Contenu du dossier", body: "Le PDF agrège : registre complet des allégations RED/ORANGE (avec verdict LLM), preuves attachées avec dates d'expiration, journal horodaté en écriture seule, comparatif sectoriel, et signature numérique EmpCo." },
      { name: "Portée", body: "Le dossier couvre tous les scans (site internet + image + document) rattachés au site sélectionné, sur toute la période de surveillance. Vous pouvez le régénérer autant de fois que nécessaire." },
      { name: "Horodatage & preuve", body: "Chaque page contient un QR code renvoyant vers l'URL de vérification EmpCo. L'horodatage est cryptographique (SHA-256 chaîné, journal immuable)." },
    ],
  },
];

export default function HowItWorksPage() {
  return (
    <div className="space-y-10 max-w-4xl" data-testid="how-it-works-page">
      <div>
        <p className="eyebrow mb-1">Guide utilisateur</p>
        <h1 className="text-3xl sm:text-4xl font-serif tracking-tight">Comment ça marche ?</h1>
        <p className="text-sm text-muted-foreground mt-3 leading-relaxed max-w-2xl">
          4 étapes pour tirer parti d'EmpCo au quotidien : vérifier la propriété d'un site, ajouter des domaines à surveiller, détecter les doublons dans votre registre et générer votre dossier de contrôle en un clic.
        </p>
      </div>

      {SECTIONS.map(({ Icon, title, intro, steps }, i) => (
        <section key={i} className="hairline p-6 rounded-lg space-y-5" data-testid={`how-section-${i}`}>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="eyebrow mb-1">Étape {i + 1}</p>
              <h2 className="text-xl sm:text-2xl font-serif tracking-tight">{title}</h2>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{intro}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-4">
            {steps.map((s, j) => (
              <div key={j} className="border border-border rounded-lg p-4 bg-accent/20">
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">{`0${j + 1}`}</p>
                <p className="font-serif text-base mb-2">{s.name}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="hairline p-6 rounded-lg flex items-center justify-between gap-4 bg-accent/40" data-testid="how-cta">
        <div>
          <p className="eyebrow mb-1">Prêt à démarrer ?</p>
          <p className="font-serif text-lg">Ajoutez votre premier site et lancez un scan de démonstration.</p>
        </div>
        <Link
          to="/app/sites"
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-5 h-11 hover:bg-primary/90 transition"
          data-testid="how-cta-btn"
        >
          <Layers className="w-4 h-4" /> Ajouter un site <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
